import moment from 'moment-timezone'

import { createGoalClientId } from './goalUtils'

export const makeGoalTarget = () => ({
    id: createGoalClientId(),
    metric: 'attempts',
    threshold: 1,
})

export const makeRemainderTargetPhase = (targets = [makeGoalTarget()]) => ({
    id: createGoalClientId(),
    kind: 'remainder',
    targets,
})

export const cloneTargetsWithNewIds = (targets = []) => targets.map((target) => ({
    ...target,
    id: createGoalClientId(),
}))

export const makeFixedTargetPhase = (targets = [makeGoalTarget()], evaluationCount = 1) => ({
    id: createGoalClientId(),
    kind: 'fixed',
    evaluationCount,
    targets,
})

export const normalizeGoalTargetPhases = (goal) => {
    if (!goal) return goal
    if (Array.isArray(goal.targetPhases) && goal.targetPhases.length > 0) {
        const { targets, ...rest } = goal
        return rest
    }

    const { targets, ...rest } = goal
    return {
        ...rest,
        targetPhases: [makeRemainderTargetPhase(
            Array.isArray(targets) && targets.length > 0 ? targets : [makeGoalTarget()]
        )],
    }
}

export const getTargetPhaseRange = (phases = [], phaseIndex) => {
    let evaluationStart = 1
    for (let index = 0; index < phaseIndex; index += 1) {
        if (phases[index]?.kind === 'fixed') {
            evaluationStart += Math.max(0, Number(phases[index].evaluationCount) || 0)
        }
    }

    const phase = phases[phaseIndex]
    const evaluationCount = phase?.kind === 'fixed' ? Number(phase.evaluationCount) : null
    return {
        evaluationStart,
        evaluationEnd: Number.isInteger(evaluationCount) && evaluationCount > 0
            ? evaluationStart + evaluationCount - 1
            : null,
    }
}

export const getTargetPhaseForPeriod = (phases = [], periodIndex = 0) => {
    for (let phaseIndex = 0; phaseIndex < phases.length; phaseIndex += 1) {
        const range = getTargetPhaseRange(phases, phaseIndex)
        if (phases[phaseIndex]?.kind === 'remainder' || periodIndex + 1 <= range.evaluationEnd) {
            return { phase: phases[phaseIndex], phaseIndex, ...range }
        }
    }
    return null
}

export const getGoalTargetKey = (target) => {
    if (target.metric === 'answerCount') return `answerCount|${target.questionId}|${target.optionId}`
    if (target.metric === 'numericSum') return `numericSum|${target.questionId}`
    return target.metric
}

export const setGoalTargetChoice = (target, encoded) => {
    const [metric, questionId, optionId] = encoded.split('|')
    Object.keys(target).forEach((key) => {
        if (key !== 'id' && key !== 'threshold') delete target[key]
    })
    target.metric = metric
    if (questionId) target.questionId = questionId
    if (optionId) target.optionId = optionId
}

const addClampedMonthsFromAnchor = (anchor, monthCount) => {
    const dayOfMonth = anchor.date()
    const targetMonth = anchor.clone().startOf('month').add(monthCount, 'months')
    return targetMonth
        .date(Math.min(dayOfMonth, targetMonth.daysInMonth()))
        .hour(anchor.hour())
        .minute(anchor.minute())
        .second(anchor.second())
        .millisecond(anchor.millisecond())
}

export const getGoalEvaluationPeriods = (goal, timezone) => {
    const start = moment.tz(`${goal?.campaignStartDate} 02:00`, 'YYYY-MM-DD HH:mm', timezone)
    const endExclusive = moment.tz(`${goal?.campaignEndDate} 02:00`, 'YYYY-MM-DD HH:mm', timezone).add(1, 'day')
    if (!start.isValid() || !endExclusive.isValid() || !endExclusive.isAfter(start)) return []
    const basisType = goal?.evaluationBasis?.type
    const interval = basisType === 'daily' ? 1 : Number(goal?.evaluationBasis?.n)
    if (!Number.isInteger(interval) || interval <= 0) return []

    const periods = []
    let periodStart = start.clone()
    let index = 0
    while (periodStart.isBefore(endExclusive) && index < 100000) {
        const nextIndex = index + 1
        let naturalEnd
        switch (basisType) {
            case 'daily':
                naturalEnd = start.clone().add(nextIndex, 'days')
                break
            case 'everyNDays':
                naturalEnd = start.clone().add(nextIndex * interval, 'days')
                break
            case 'everyNWeeks':
                naturalEnd = start.clone().add(nextIndex * interval, 'weeks')
                break
            case 'everyNMonths':
                naturalEnd = addClampedMonthsFromAnchor(start, nextIndex * interval)
                break
            default:
                return []
        }

        const periodEnd = naturalEnd.isAfter(endExclusive) ? endExclusive.clone() : naturalEnd
        periods.push({ index, start: periodStart.clone(), end: periodEnd })
        periodStart = naturalEnd
        index += 1
    }
    return periods
}

export const getTargetPhaseDateRange = (goal, phaseIndex, timezone) => {
    const phases = goal?.targetPhases || []
    const range = getTargetPhaseRange(phases, phaseIndex)
    const periods = getGoalEvaluationPeriods(goal, timezone)
    const firstPeriod = periods[range.evaluationStart - 1]
    if (!firstPeriod) return { ...range, reached: false }

    const lastPeriodIndex = range.evaluationEnd === null
        ? periods.length - 1
        : Math.min(periods.length - 1, range.evaluationEnd - 1)
    return {
        ...range,
        reached: true,
        start: firstPeriod.start,
        end: periods[lastPeriodIndex].end,
    }
}

export const formatTargetPhasePosition = (targetPhase) => {
    if (!targetPhase) return ''
    const phaseLabel = targetPhase.kind === 'remainder' ? 'Rest' : `Phase ${targetPhase.phaseNumber}`
    if (targetPhase.kind === 'remainder') {
        return `${phaseLabel} · Evaluation ${targetPhase.evaluationNumber}`
    }
    return `${phaseLabel} · Evaluation ${targetPhase.evaluationNumber} · ${targetPhase.evaluationNumberInPhase} of ${targetPhase.evaluationCount}`
}
